import React from "react";
import EnumSelectorInput from "../../common/inputs/EnumSelectorInput";
import ValueReset from "../../common/inputs/ValueReset";
import { DatabaseContext } from "../../database/DatabaseHandler";
import { IDrawerSystemParamTypeDescriptor } from "../../database/interfaces/paramScript/IParamTypeDescriptor";
import { fieldAccess } from "../../parametersProcessor/paramAccess";
import { fieldDefault } from "../../parametersProcessor/paramDefaults";

interface IDrawerSystemCardContentProps {
    typeDescriptor: IDrawerSystemParamTypeDescriptor;
    paramValue: any;
    storedDefault?: any;
    onUpdateParamValue: (value: any) => void;
}

const NONE = "None";

const seriesOf = (value: any): string | undefined =>
    value && typeof value === "object" ? value.DrawerSystemSeries : undefined;

/**
 * Fiókrendszer-választó: csak a sorozat (a `/drawer_systems` listából); a konkrét rendszert a Unity
 * választja. Az érték `{"DrawerSystemSeries": <sorozat>}`, amit a descriptor `flatten`-je a wrapper
 * gyökerére tesz. A „nincs" (descriptor `none`) üres objektum, amit a generateStyleScript ki sem küld —
 * ugyanúgy, mint a vasalat-választónál.
 */
const DrawerSystemCardContent: React.FC<IDrawerSystemCardContentProps> = ({
    typeDescriptor, paramValue, storedDefault, onUpdateParamValue,
}) => {
    const d = typeDescriptor;
    const { drawerSystemData } = React.useContext(DatabaseContext);
    const systems = drawerSystemData ?? [];
    const access = fieldAccess(d);

    const options = React.useMemo(
        () => Object.fromEntries([
            ...(d?.none ? [[NONE, d.none]] : []),
            ...systems.map((s) => [s.Series, `${s.Manufacturer} ${s.Series}`]),
        ]),
        [systems, d?.none]
    );
    const keyOf = (value: any) => seriesOf(value) ?? (d?.none ? NONE : "");

    const emit = (key: string) => onUpdateParamValue(key === NONE ? {} : { DrawerSystemSeries: key });

    return d ? (
        <ValueReset
            paramDefault={access.resettable ? fieldDefault({ options, default: keyOf(storedDefault) || undefined }, undefined) : null}
            currentValue={keyOf(paramValue)}
            onReset={emit}
        >
            <EnumSelectorInput
                label={d.label}
                value={keyOf(paramValue)}
                options={options}
                tooltip={d.tooltip}
                editable={access.editable}
                readOnly={access.readOnly}
                onValueChange={emit}
            />
        </ValueReset>
    ) : null;
};

export default DrawerSystemCardContent;
